import React, { useState } from 'react';
import { Button } from './button';

interface UnsavedChangesDialogProps {
  isOpen: boolean;
  title?: string;
  message?: string;
  onSave?: () => Promise<void> | void;
  onDiscard: () => void;
  onCancel: () => void;
  saveLabel?: string;
  discardLabel?: string;
  cancelLabel?: string;
  showSaveOption?: boolean;
}

export function UnsavedChangesDialog({
  isOpen,
  title = 'Unsaved Changes',
  message = 'You have unsaved changes. What would you like to do?',
  onSave,
  onDiscard,
  onCancel,
  saveLabel = 'Save Changes',
  discardLabel = 'Discard Changes',
  cancelLabel = 'Cancel',
  showSaveOption = true,
}: UnsavedChangesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!onSave) return;
    
    setIsSaving(true);
    try {
      await onSave();
    } catch (error) {
      console.error('Failed to save changes:', error);
      // Let the parent component handle the error
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          </div>
        </div>
        
        <div className="mb-6">
          <p className="text-sm text-gray-600">{message}</p>
        </div>
        
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            {cancelLabel}
          </Button>
          
          <Button
            variant="destructive"
            onClick={onDiscard}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            {discardLabel}
          </Button>
          
          {showSaveOption && onSave && (
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {isSaving ? 'Saving...' : saveLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}